import React, { useContext, useEffect, useState } from 'react';
import { getConfig } from '@edx/frontend-platform';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';

import { useHeaderFooter, useCoursePreview, useProfileCourses } from '../../hooks';
import { addInstructorsGridControl, isCourseA5DayChallenge, isEligibleToEnrollV2 } from '../../utils';
import { enrollmentsApi } from '../../api';
import { AppDataContext } from '../../context';
import {
  SpinLoader,
  ModuleOutline,
  CourseAccessButton,
  CarouselCourseCard,
  CourseUnavailable,
} from '../../components';

import './index.scss';
import messages from './messages';

import tlunaImg from '../../img/tluna-pic.jpeg';
import line612Img from '../../img/courseOverview/line-6-12.svg';
import line53Img from '../../img/courseOverview/line-5-3.svg';
import arrow21Img from '../../img/courseOverview/arrow-2-1.svg';
import simbeKadarakeImg from '../../img/courseOverview/SimbeKadarake.jpg';
import layer17Img from '../../img/courseOverview/layer-17.svg';

interface CourseOverviewProps {
  children?: React.ReactNode;
}

const CourseOverview: React.FC<CourseOverviewProps> = ({ children }) => {
  const [formattedCourseName, setFormattedCourseName] = useState<string[]>([]);
  const [eligibleToEnroll, setEligibleToEnroll] = useState<boolean>(false);
  const [courseCompleted, setCourseCompleted] = useState<boolean>(false);
  const [Loaded, setLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { headerContent, footerContent } = useHeaderFooter(children);
  const { authenticatedUser, allowedCourses, allowedCoursesLoaded } = useContext(AppDataContext);
  const { getCourseCompletions, getCourseGrade, isUserCoursesCompletionsLoaded } = useProfileCourses(authenticatedUser);

  const intl = useIntl();
  const { courseID = '' } = useParams();
  const navigate = useNavigate();
  const { LMS_BASE_URL } = getConfig();
  const firstOfAllowedCourseId = allowedCourses[0]?.course_id;
  const lastOfAllowedCourseId = allowedCourses[allowedCourses.length - 1]?.course_id;

  const {
    courseInfo,
    courseInfoLoaded,
    courseInfoErrorStatus,
  } = useCoursePreview(courseID);

  const isChallenge = isCourseA5DayChallenge(courseID, false, allowedCourses);
  const challengeIndex = isChallenge ? allowedCourses.findIndex((c) => c.course_id === courseID) : -1;
  const parentCourseID = challengeIndex > 0 ? allowedCourses[challengeIndex - 1].course_id : '';
  const { courseInfo: parentCourseInfo } = useCoursePreview(parentCourseID);

  const isCourseAllowed = allowedCourses.some(({ course_id: id }) => id === courseID);

  const handleCourseNameFormatting = () => {
    const courseName = courseInfo?.display_name;
    const separator = '5 Day'; // TODO: refactoring is necessary after implementing localization

    if (String(courseName).includes(separator)) {
      setFormattedCourseName(String(courseName).split(separator));
    }
  };

  const enrollUser = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();

    if (!authenticatedUser?.userId) {
      window.location.href = `/login?redirect-to=${window.location.href}`;
      return;
    }

    const isCurrentlyEnrolled = await enrollmentsApi.getUserEnrollmentStatus(courseID || '');

    if (isCurrentlyEnrolled) {
      navigate(`/course-working/${courseID}`);
      return;
    }

    setIsLoading(true);
    try {
      await enrollmentsApi.enrollUserToCourse(courseID || '', () => {
        setIsLoading(false);
        navigate(`/course-working/${courseID}`);
      });
    } catch (error) {
      setIsLoading(false);
      logError('Enrollment failed', error);
    }
  };

  useEffect(() => {
    if (courseInfoLoaded) {
      handleCourseNameFormatting();
      try {
        addInstructorsGridControl();
      } catch (error: unknown) {
        logError('failed adding instructor slider controls', error);
      }
    }
  }, [courseID, courseInfo, courseInfoLoaded]);

  useEffect(() => {
    if (courseID === firstOfAllowedCourseId) {
      setEligibleToEnroll(true);
      setLoaded(true);
    } else {
      const effectivePrerequisiteSource = isChallenge ? parentCourseInfo : courseInfo;
      const preCourseId = effectivePrerequisiteSource?.pre_requisite_courses?.[0];
      const preCourseCompletion = getCourseCompletions(preCourseId);
      const preCourseGrade = getCourseGrade(preCourseId);
      isEligibleToEnrollV2(
        preCourseId,
        setLoaded,
        true,
        authenticatedUser,
        preCourseCompletion,
        preCourseGrade,
      )
        .then((eligibleData) => {
          if (typeof eligibleData !== 'boolean') {
            setEligibleToEnroll(eligibleData.eligible);
          }
        });
    }

    const courseCompletion = getCourseCompletions(courseID);
    const courseGrade = getCourseGrade(courseID);
    isEligibleToEnrollV2(courseID, setLoaded, true, authenticatedUser, courseCompletion, courseGrade)
      .then((eligibleData) => {
        if (typeof eligibleData !== 'boolean') {
          setCourseCompleted(eligibleData.completed);
        }
      });
  }, [courseID, courseInfo, parentCourseInfo, isUserCoursesCompletionsLoaded]);

  const isCourseUnavailable = allowedCoursesLoaded
    && (!isCourseAllowed || (courseInfoLoaded && courseInfoErrorStatus === 404));

  let bodyOverride: React.ReactNode = null;
  if (!allowedCoursesLoaded) {
    bodyOverride = <SpinLoader size={50} containerHeight="40vh" />;
  } else if (isCourseUnavailable) {
    bodyOverride = <CourseUnavailable />;
  }

  return (
    <>
      <input
        type="hidden"
        id="anPageName"
        name="page"
        defaultValue="course-overview"
      />
      <div className="container-center-horizontal">
        <div className="course-overview screen">
          <div className="homepage">
            <div className="overlap-group8">
              <div className="hero-image">
                <div
                  className="overlap-group"
                  style={{
                    backgroundImage: `url('${LMS_BASE_URL}${courseInfo?.banner_image_asset_path}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                  }}
                >
                  <div className="rectangle-20 rectangle" />
                </div>
              </div>
              <div className="headline">
                <h1 className="course-01-why-lead instrumentserif-normal-white-100-1px-2">
                  <span className="instrumentserif-normal-white-100-1px d-block">
                    {intl.formatMessage(messages.module)}
                  </span>
                  {formattedCourseName.length > 0 ? (
                    <span className="recklessneue-regular-normal-white-100-1px course-name">
                      {formattedCourseName[0]}
                      <br />
                      {intl.formatMessage(messages.fiveDayNamePart)}
                      {' '}
                      {formattedCourseName[1]}
                    </span>
                  ) : (
                    <span className="recklessneue-regular-normal-white-100-1px course-name">
                      {courseInfo?.display_name}
                    </span>
                  )}
                </h1>
              </div>
              {headerContent}
            </div>
          </div>
          {bodyOverride ?? (
            <>
              <div className="page-nav-custom">
                <div className="flex-col-1 flex-col-8" style={{ margin: 'auto' }}>
                  <p className="modules-01-why-lead recklessneue-book-normal-congress-blue-22-8px">
                    {intl.formatMessage(messages.modules)} &gt; {' '}
                    {
                  formattedCourseName.length > 0 ? (
                    <span className="course-name">
                      {formattedCourseName[0]}
                      <br />
                      {intl.formatMessage(messages.fiveDayNamePart)}
                      {' '}
                      {formattedCourseName[1]}
                    </span>
                  ) : (
                    <span className="course-name">{courseInfo?.display_name}</span>
                  )
                }
                  </p>{' '}
                  <img className="line-6" src={line612Img} alt="" />
                </div>
                {courseInfoLoaded && (
                <CourseAccessButton
                  duration={courseInfo?.duration}
                  Loaded={Loaded}
                  eligibleToEnroll={eligibleToEnroll}
                  courseCompleted={courseCompleted}
                  isLoading={isLoading}
                  isGuest={!authenticatedUser?.userId}
                  handleEnrollUser={enrollUser}
                />
                )}
              </div>
              <div className="course-overview-text course-overview-text-custom">
                <div className="intro-text-01">
                  <div className="x01-why-lead-overview recklessneue-book-normal-congress-blue-48px">
                    <span className="course-name">{courseInfo?.display_name}</span>
                    <br />
                    {intl.formatMessage(messages.overview)}
                  </div>
                  <div className="in-this-module-you neuehaasgroteskdisplaypro-45-light-congress-blue-24px what-p about-p">
                    {/* eslint-disable-next-line react/no-danger */}
                    <div dangerouslySetInnerHTML={{ __html: courseInfo?.overview }} />
                  </div>
                </div>
                <div className="overlap-group-1">
                  <p className="the-4-pillars-of-suc neuehaasgroteskdisplaypro-45-light-congress-blue-24px course-material" />
                  <p className="what-you-will-learn-in-this-course recklessneue-book-normal-congress-blue-48px">
                    {intl.formatMessage(messages.inThisModule)}
                  </p>
                  <p className="identify-your-influe neuehaasgroteskdisplaypro-45-light-congress-blue-24px what-p learning-p" />
                  <ul className="l-outcome">
                    {courseInfoLoaded && (
                      courseInfo?.learning_info?.map((outcome: string) => (
                        <li key={outcome}>
                          {outcome}
                        </li>
                      ))
                    )}
                  </ul>
                  <p />
                </div>
              </div>
              <div className="tutors">
                <div className="flex-row flex">
                  <div className="overlap-group7 overlap-group7-custom">
                    <p className="meet-your-guidesfor-this-course recklessneue-book-normal-congress-blue-48px">
                      {intl.formatMessage(messages.meetYourGuides, { lineBreak: <br /> })}
                    </p>
                    <div className="tutors-full">
                      <div className="tutors-grid tutors-grid-custom">
                        {courseInfoLoaded && courseInfo?.instructor_info?.instructors?.map((instructor: any) => (
                          <div key={instructor.title}>
                            <NavLink to={`/profile-guide/${instructor.title}`}>
                              <div className="badge-01 badge">
                                <div
                                  className="frame-30"
                                  style={{
                                    backgroundImage: `url("${instructor.title === 'tluna' ? tlunaImg : LMS_BASE_URL + instructor.image}")`,
                                  }}
                                />
                                <div className="name neuehaasgroteskdisplaypro-45-light-congress-blue-38px">
                                  {instructor.name}
                                </div>
                              </div>
                            </NavLink>
                          </div>
                        ))}
                      </div>
                      <div className="courses-slider-control">
                        <div className="overlap-group-2">
                          <img className="line-5" src={line53Img} alt="" style={{ display: 'none' }} />
                          <div className="rectangle-7 rectangle" style={{ width: '25%' }} />
                        </div>
                        <div className="arrow-container">
                          <img
                            className="arrow previous_tutor disabled"
                            src={arrow21Img}
                            alt=""
                          />
                          <img
                            className="arrow next_tutor"
                            src={arrow21Img}
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="start-module-btn-container">
                {Loaded && eligibleToEnroll && (
                  isLoading ? (
                    <SpinLoader size={40} containerHeight="150px" />
                  ) : (
                    <NavLink
                      to="#"
                      onClick={enrollUser}
                      className="course-btn"
                    >
                      {intl.formatMessage(messages.startTheModule)}
                    </NavLink>
                  )
                )}
              </div>
              <div className="course-chapters">
                <div className="overlap-group6">
                  <div className="syl-cont">
                    <p className="x01-why-lead-course-chapters">
                      <span className="instrumentserif-normal-congress-blue-48px">
                        {intl.formatMessage(messages.moduleLabel)}{' '}
                      </span>
                      <span className="span1 course-name">{courseInfo?.display_name}</span>
                    </p>
                    <ModuleOutline courseInfo={courseInfo} courseInfoLoaded={courseInfoLoaded} />
                  </div>
                </div>
              </div>
              <div className="qoute-block">
                <div className="overlap-group5">
                  <div className="overlap-group4">
                    <div className="overlap-group3">
                      <p className="the-course-has-reall recklessneue-book-normal-congress-blue-48px">
                        {intl.formatMessage(messages.quote)}
                      </p>
                    </div>
                    <div className="test-2">
                      <img
                        src={simbeKadarakeImg}
                        alt={intl.formatMessage(messages.quoteAuthorImageAlt)}
                        style={{ borderRadius: '50%', marginBottom: '20px' }}
                      />
                      <div className="simbe-kadarake instrumentserif-normal-congress-blue-48px">
                        -{intl.formatMessage(messages.quoteAuthor)}
                      </div>
                    </div>
                  </div>
                  <img className="layer_17" src={layer17Img} alt="" />
                </div>
              </div>
              {courseID !== lastOfAllowedCourseId && (
              <div className="overlap-group9">
                <div className="following-chapters">
                  <div className="next-courses recklessneue-book-normal-congress-blue-48px">
                    {intl.formatMessage(messages.nextModules)}
                  </div>
                  <div className="courses">
                    <div className="course-container course-container-overview">
                      <CarouselCourseCard courseID={courseID} />
                    </div>
                  </div>
                </div>
              </div>
              )}
            </>
          )}
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default CourseOverview;
